import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

import { getLogo } from '../../api/aws/s3';
import { companiesRef } from '../../api/firebase/firestore/company';
import { usersRef } from '../../api/firebase/firestore/user';
import {
  black,
  primarycolor,
  secondaryButtonnBackground,
  tertiaryButtonBackground
} from '../../constants/Colors';
import { useIndustry } from '../../constants/Select';
import { select } from '../../constants/enum';
import { AuthContext } from '../../contexts/auth';
import { throwError } from '../../modules/error';

import CustomLoading from './CustomLoading';
import CustomSnackbar from './CustomSnackbar';
import NothingGivenList from './NothingGivenList';

import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';

const styles = {
  avatarMl: {
    marginLeft: '24px'
  },
  button: {
    '&:hover': {
      backgroundColor: '#000000',
      color: '#fff',
      opacity: 0.35
    },
    backgroundColor: '#000000',
    color: '#fff',
    marginRight: '15px',
    opacity: 0.5
  },
  buttonFont: {
    fontSize: '13px'
  },
  buttonLocation: {
    height: '0',
    position: 'sticky',
    textAlign: 'right',
    top: '15px'
  },
  card: {
    border: '1px solid #DBDBE0',
    borderRadius: '4px',
    boxShadow: 0,
    margin: '24px'
  },
  circle: {
    bgcolor: '#DBDBE0',
    borderRadius: '50%',
    height: '40px',
    width: '40px'
  },
  companyName: {
    '@media screen and (max-width:480px)': { fontSize: '16px' },
    color: black,
    fontSize: '20px',
    fontWeight: 700
  },
  contactButton: {
    '@media screen and (max-width:960px)': {
      height: '30px',
      padding: '4px 10px'
    },
    padding: '6px 16px'
  },
  contactButtonText: {
    color: black,
    fontSize: '14px'
  },
  content: {
    textAlign: 'left'
  },
  description: {
    '@media screen and (max-width:480px)': { fontSize: '12px' },
    color: black,
    fontSize: '14px',
    marginTop: '30px'
  },
  divider: {
    backgroundColor: '#DBDBE0',
    color: black,
    marginBottom: '24px',
    marginTop: '24px'
  },
  fontMd: {
    color: black,
    fontSize: '14px'
  },
  fontSm: {
    fontSize: '12px'
  },
  heading: {
    fontWeight: 'bold'
  },
  media: {
    height: '180px',
    marginTop: '30'
  },
  subheading: {
    lineHeight: 1.8
  },
  tableCell: {
    alignItems: 'center',
    width: '50px'
  },
  textMt: {
    marginTop: '16px'
  }
};

export default function TableInTransaction(): JSX.Element {
  const { t } = useTranslation();
  const { currentUser } = useContext(AuthContext);
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [showList, setShowList] = React.useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) {
      setOpen(true);
      setErrorText(t('form.loginError'));
      setTimeout(() => {
        navigate('/auth/login');
      }, 6000);
    }

    void (async () => {
      await init();
    })();
  }, []);

  const [rows, setRows] = useState<
    Array<{
      id: string;
      icon: string;
      name: string;
      industry: string;
      url: string;
    }>
  >([]);

  const industryList = useIndustry();

  const init = async (): Promise<void> => {
    setLoading(true);
    try {
      if (currentUser) {
        const userRef = doc(usersRef, currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const ownCampanyRef = doc(
            companiesRef,
            userSnap.data()?.companyId as string
          );
          const companySnap = await getDoc(ownCampanyRef);

          const companies = query(
            companiesRef,
            where('agentFlg', '==', true),
            where('agentLists', '==', [{ companyId: companySnap.id }])
          );
          const companiesSnapshots = await getDocs(companies);
          companiesSnapshots.docs.forEach(
            (company: QueryDocumentSnapshot<DocumentData>): void => {
              void (async (): Promise<void> => {
                const icon = await getLogo(company);
                const industryFind = industryList.find(
                  (element): boolean =>
                    element.value === (company.data()?.industry as number)
                )?.option;

                const industry: string | undefined =
                  select.other === (company.data()?.industry as number)
                    ? (company.data()?.industryOther as string)
                    : industryFind;
                setRows((prevState) => [
                  ...prevState,
                  {
                    icon,
                    id: company.id,
                    industry: industry ?? '',
                    name: company.data()?.name as string,
                    url: company.data()?.url as string
                  }
                ]);
              })();
            }
          );
        }
      }
    } catch (e) {
      throwError(e);
    } finally {
      setLoading(false);
      setShowList(true);
    }
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Grid container>
        <Grid item xs={12} sx={{ overflow: 'scroll' }}>
          {showList && (
            <Typography component="div">
              {rows.length > 0 ? (
                <Card sx={[styles.card, { minWidth: '1136px' }]}>
                  <CardContent sx={styles.content}>
                    <Grid container>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell align="right"></TableCell>
                            <TableCell>
                              {t('registerBasic.companyName')}
                            </TableCell>
                            <TableCell align="left">
                              {t('form.industry')}
                            </TableCell>
                            <TableCell align="left">{t('form.url')}</TableCell>
                            <TableCell align="right"></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map((row, index) => (
                            <TableRow
                              key={index}
                              sx={{
                                '&:last-child td, &:last-child th': {
                                  border: 0
                                }
                              }}
                            >
                              <TableCell
                                sx={styles.tableCell}
                                component="th"
                                scope="row"
                              >
                                <Avatar key={row.icon} src={row.icon} />
                              </TableCell>
                              <TableCell>
                                <Typography>{row.name}</Typography>
                              </TableCell>
                              <TableCell>{row.industry}</TableCell>
                              <TableCell align="left" sx={{ width: '200px' }}>
                                <Link href={row.url}>{row.url}</Link>
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ minWidth: '250px' }}
                              >
                                <Button
                                  variant="outlined"
                                  sx={[
                                    tertiaryButtonBackground,
                                    styles.contactButton
                                  ]}
                                  onClick={() =>
                                    navigate('/agent/client/basic', {
                                      state: {
                                        id: row.id
                                      }
                                    })
                                  }
                                >
                                  <Typography
                                    component="span"
                                    sx={styles.contactButtonText}
                                  >
                                    {t('given.edit')}
                                  </Typography>
                                </Button>
                                <Button
                                  variant="contained"
                                  sx={[
                                    secondaryButtonnBackground,
                                    { background: 'white', ml: '36px' }
                                  ]}
                                  onClick={() =>
                                    navigate(
                                      '/agent/mypage/issue/edit?type=add',
                                      {
                                        state: {
                                          id: row.id,
                                          type: 'agent'
                                        }
                                      }
                                    )
                                  }
                                >
                                  <Typography
                                    component="div"
                                    color={primarycolor}
                                    sx={styles.buttonFont}
                                  >
                                    {t('given.register')}
                                  </Typography>
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Grid>
                  </CardContent>
                </Card>
              ) : (
                <NothingGivenList
                  title={t('given.nothingTitle')}
                  detail={t('given.nothingDetail')}
                />
              )}
            </Typography>
          )}
        </Grid>
      </Grid>
      <CustomSnackbar
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        text={errorText}
      />
    </>
  );
}
